<template>
  <div v-if="hasActiveJobs" class="pdf-job-manager">
    <div class="job-manager-header">
      <div class="header-content">
        <font-awesome-icon :icon="['fas', 'file-pdf']" class="header-icon" />
        <button 
          v-if="hasCompletedJobs"
          class="cleanup-btn"
          title="Nettoyer les jobs terminés"
          @click="cleanupCompletedJobs"
        >
          <font-awesome-icon :icon="['fas', 'broom']" />
        </button>
      </div>
    </div>

    <div class="jobs-list">
      <div 
        v-for="job in sortedJobs" 
        :key="job.jobId"
        class="job-item"
        :class="{
          'job-pending': job.status === 'pending',
          'job-processing': job.status === 'processing',
          'job-completed': job.status === 'completed',
          'job-failed': job.status === 'failed'
        }"
      >
        <div class="job-info">
          <div class="job-header">
            <span class="job-name">{{ job.supportName }}</span>
            <div class="job-actions">
              <button
                v-if="job.status === 'completed' && job.downloadUrl"
                class="download-btn"
                title="Télécharger le PDF"
                @click="downloadPdf(job.jobId)"
              >
                <font-awesome-icon :icon="['fas', 'download']" />
              </button>
              <button
                class="remove-btn"
                :title="job.status === 'completed' || job.status === 'failed' ? 'Supprimer' : 'Annuler'"
                @click="removeJob(job.jobId)"
              >
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </div>
          </div>

          <div class="job-status">
            <div class="status-info">
              <font-awesome-icon 
                :icon="getStatusIcon(job.status)"
                :class="`status-icon status-${job.status}`"
              />
              <span class="status-text">{{ getStatusText(job.status) }}</span>
              <span v-if="job.status === 'processing'" class="progress-text">
                ({{ job.progress }}%)
              </span>
            </div>
            <div class="job-time">
              {{ formatJobTime(job.createdAt) }}
            </div>
          </div>

          <div 
            v-if="job.status === 'processing'"
            class="progress-bar"
          >
            <div 
              class="progress-fill"
              :style="{ width: `${job.progress}%` }"
            />
          </div>

          <div 
            v-if="job.status === 'failed' && job.error"
            class="job-error"
          >
            {{ job.error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePdfJobManager } from '@/composables/supports/usePdfJobManager';
import { computed } from '@vue/runtime-core';

const { activeJobs, downloadPdf, removeJob, cleanupOldJobs } = usePdfJobManager();

// Computed properties
const sortedJobs = computed(() => {
  return Array.from(activeJobs.value.values())
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
});

const hasActiveJobs = computed(() => {
  return activeJobs.value.size > 0;
});

const hasCompletedJobs = computed(() => {
  return Array.from(activeJobs.value.values())
    .some(job => job.status === 'completed' || job.status === 'failed');
});

// Methods
const getStatusIcon = (status: string): [string, string] => {
  switch (status) {
    case 'pending':
      return ['fas', 'clock'];
    case 'processing':
      return ['fas', 'spinner'];
    case 'completed':
      return ['fas', 'check-circle'];
    case 'failed':
      return ['fas', 'exclamation-circle'];
    default:
      return ['fas', 'question-circle'];
  }
};

const getStatusText = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'En attente';
    case 'processing':
      return 'En cours';
    case 'completed':
      return 'Terminé';
    case 'failed':
      return 'Échec';
    default:
      return 'Inconnu';
  }
};

const formatJobTime = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 1) {
    return 'À l\'instant';
  } else if (minutes < 60) {
    return `Il y a ${minutes} min`;
  } else {
    const hours = Math.floor(minutes / 60);
    return `Il y a ${hours}h${minutes % 60}min`;
  }
};

const cleanupCompletedJobs = () => {
  const jobsToRemove = Array.from(activeJobs.value.entries())
    .filter(([_, job]) => job.status === 'completed' || job.status === 'failed')
    .map(([jobId]) => jobId);
  
  jobsToRemove.forEach(jobId => removeJob(jobId));
};
</script>

<style scoped>
.pdf-job-manager {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 350px;
  max-height: 500px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.job-manager-header {
  background: linear-gradient(135deg, #b70018 0%, #8c0014 100%);
  color: white;
  padding: 16px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  font-size: 18px;
}

.header-title {
  flex: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.cleanup-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  padding: 8px;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cleanup-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.jobs-list {
  max-height: 400px;
  overflow-y: auto;
}

.job-item {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s ease;
}

.job-item:last-child {
  border-bottom: none;
}

.job-item:hover {
  background-color: #f9fafb;
}

.job-pending {
  border-left: 4px solid #fbbf24;
}

.job-processing {
  border-left: 4px solid #3b82f6;
}

.job-completed {
  border-left: 4px solid #10b981;
}

.job-failed {
  border-left: 4px solid #ef4444;
}

.job-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.job-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  flex: 1;
  margin-right: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-actions {
  display: flex;
  gap: 4px;
}

.download-btn,
.remove-btn {
  background: none;
  border: none;
  padding: 6px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.download-btn {
  color: #10b981;
}

.download-btn:hover {
  background-color: #ecfdf5;
  color: #059669;
}

.remove-btn {
  color: #6b7280;
}

.remove-btn:hover {
  background-color: #fef2f2;
  color: #dc2626;
}

.job-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.status-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-icon {
  font-size: 14px;
}

.status-pending {
  color: #f59e0b;
}

.status-processing {
  color: #3b82f6;
  animation: spin 1s linear infinite;
}

.status-completed {
  color: #10b981;
}

.status-failed {
  color: #ef4444;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.status-text {
  font-size: 13px;
  color: #6b7280;
}

.progress-text {
  font-size: 12px;
  color: #9ca3af;
}

.job-time {
  font-size: 11px;
  color: #9ca3af;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background-color: #f3f4f6;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.job-error {
  margin-top: 8px;
  padding: 8px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 12px;
  line-height: 1.4;
}

/* Scrollbar personnalisée */
.jobs-list::-webkit-scrollbar {
  width: 6px;
}

.jobs-list::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.jobs-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.jobs-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>